<template>
  <div>
    <b-row class="mb-1">
      <!-- search input -->
      <b-col>
        <section>
          <b-button
            v-b-modal.modal-filter-refundqueue
            variant="outline-primary"
            class="d-flex text-primary"
          >
            <feather-icon icon="FilterIcon" />Filter
          </b-button>
        </section>
      </b-col>
    </b-row>
    <b-card title="Refund Queue">
      <!-- search input -->
      <b-row v-if="hasData" class="mb-1">
        <b-col class="d-flex justify-content-start">
          <b-form-group>
            <b-form-input
              v-model="searchTerm"
              placeholder="Search"
              type="search"
            />
          </b-form-group>
        </b-col>
        <b-col />
        <b-col class="d-flex justify-content-end">
          <xlsx-workbook>
            <xlsx-sheet
              v-for="sheet in sheets"
              :key="sheet.name"
              :collection="sheet.data"
              :sheet-name="sheet.name"
            />
            <xlsx-download>
              <b-button variant="primary" class="d-flex">
                <feather-icon icon="DownloadCloudIcon" /> Export
              </b-button>
            </xlsx-download>
          </xlsx-workbook>
        </b-col>
      </b-row>

      <!-- table -->
      <vue-good-table
        :columns="columns"
        :rows="rows"
        :rtl="direction"
        :search-options="{
          enabled: true,
          externalQuery: searchTerm,
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
      >
        <template slot="table-row" slot-scope="props">
          <!-- Column: Name -->

          <!-- Column: Status -->
          <span v-if="props.column.field === 'refundStatus'">
            <span>
              <b-badge v-if="props.row.refundStatus === 0" pill variant="info">
                Not Requested
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 1"
                pill
                variant="warning"
                class="text-light"
              >
                Pending
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 2"
                pill
                variant="light-success"
                class="text-light"
              >
                Submitted
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 3"
                pill
                variant="success"
                class="text-light"
              >
                Approved
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 4"
                pill
                variant="light-info"
                class="text-light"
              >
                Sent
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 5"
                pill
                variant="danger"
                class="text-light"
              >
                Rejected
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 6"
                pill
                variant="primary"
                class="text-light"
              >
                Completed
              </b-badge>
              <b-badge v-else pill variant="info" class="text-light">
                {{ props.row.refundStatus }}
              </b-badge>
            </span>
          </span>

          <b-link
            v-else-if="props.column.field === 'refundReferenceCode'"
            :to="{
              name: 'refundQueue-details',
              params: {
                refundrequest: props.row,
                id: props.row.id,
              },
            }"
          >
            <feather-icon icon="EyeIcon" />
            <span class="align-middle ml-50">{{
              props.row.refundReferenceCode
            }}</span>
          </b-link>

          <!-- Column: Action -->
          <!-- <span v-else-if="props.column.field === 'action'">
            <b-button
              :to="{
                name: 'refund-handler',
                params: {
                  refundQueue: props.row,
                  id: props.row.id,
                },
              }"
              variant="primary"
              class="d-flex"
            >
              <feather-icon icon="ActivityIcon" />
              <span class="align-middle ml-50 text-light">Approve/Reject</span>
            </b-button>
          </span> -->

          <span v-else-if="props.column.label === 'Approver Name'">
            <span>
              {{
                props.row.approverFirstName + " " + props.row.approverLastName
              }}
            </span>
          </span>

          <!-- Column: Common -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap"> Showing 1 to </span>
              <b-form-select
                v-model="pageLength"
                :options="['5', '10', '15', '30', '50']"
                class="mx-1"
                @input="
                  (value) => props.perPageChanged({ currentPerPage: value })
                "
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>

      <!-- loadings -->
      <b-overlay :show="isLoading" no-wrap rounded="sm" />
    </b-card>
    <b-modal
      id="modal-filter-refundqueue"
      title="Filter Refund Report"
      ok-title="Filter"
      ok-only
      size="lg"
      @ok="filterRefundQueue"
    >
      <b-form>
        <b-row>
          <b-col md="6">
            <b-form-group label="Custom Order Code">
              <b-form-input
                id="filtercustomOrderCode-input"
                v-model="filtercustomOrderCode"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Flygate Order Id">
              <b-form-input
                id="filterflyGateOrderId-input"
                v-model="filterflyGateOrderId"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Paid FOP">
              <b-form-select
                v-model="selectedPaidPaymentOptions"
                :options="paidPaymentOptions"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Vendor TxnRef Number">
              <b-form-input
                id="filtervendorTxnRefNumber-input"
                v-model="filtervendorTxnRefNumber"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Refund Merchant Request Code">
              <b-form-input
                id="filterrefundMerchantRequestCode-input"
                v-model="filterrefundMerchantRequestCode"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Refund Reference Code">
              <b-form-input
                id="filterrefundReferenceCode-input"
                v-model="filterrefundReferenceCode"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Refund Request Date Range">
              <flat-pickr
                v-model="trxnDateRangeRefund"
                class="form-control"
                :config="{ mode: 'range', maxDate: 'today' }"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Paid Date Range">
              <flat-pickr
                v-model="trxnDateRangePaidDate"
                class="form-control"
                :config="{ mode: 'range', maxDate: 'today' }"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="From Amount">
              <b-form-input
                id="fromRequestedAmount-input"
                v-model.number="fromRequestedAmount"
                type="number"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="To Amount">
              <b-form-input
                id="toRequestedAmount-input"
                v-model.number="toRequestedAmount"
                type="number"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BAvatar,
  BBadge,
  BPagination,
  BOverlay,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdown,
  BDropdownItem,
  BRow,
  BButton,
  BLink,
  BCol,
  BForm,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import flatPickr from "vue-flatpickr-component";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axios from "@axios";
import store from "@/store";
import XlsxSheet from "vue-xlsx/dist/components/XlsxSheet";
import XlsxDownload from "vue-xlsx/dist/components/XlsxDownload";
import XlsxWorkbook from "vue-xlsx/dist/components/XlsxWorkbook";

export default {
  name: "Users",
  components: {
    BRow,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BOverlay,
    BPagination,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    flatPickr,
    BButton,
    BLink,
    BCol,
    XlsxWorkbook,
    XlsxSheet,
    XlsxDownload,
  },
  data() {
    const nowDate = new Date();
    return {
      isLoading: false,
      hasData: false,
      rows: [],
      pageLength: 5,
      isDeleteModel: false,
      dir: false,
      rangeDate: null,
      activeRow: null,
      extendedDate: `${nowDate.getUTCFullYear()}-${
        nowDate.getUTCMonth() + 1
      }-${nowDate.getUTCDate()}`,
      trxnDateRangeRefund: "",
      trxnDateRangePaidDate: "",
      paidStartDate: "",
      paidEndDate: "",
      refundStartDate: "",
      refundEndDate: "",
      filterflyGateOrderId: "",
      filtervendorTxnRefNumber: "",
      filtercustomOrderCode: "",
      fromRequestedAmount: 0,
      toRequestedAmount: 0,
      filtertrnxDate: "",
      filterrefundReferenceCode: "",
      filterrefundMerchantRequestCode: "",
      selectedPaidPaymentOptions: 0,
      columns: [
        {
          label: "Refund Requested Date",
          field: "refundRequestedDateString",
        },
        {
          label: "First Name",
          field: "firstName",
        },
        {
          label: "Last Name",
          field: "lastName",
        },
        {
          label: "Custom Order Code",
          field: "customOrderCode",
        },
        {
          label: "FlyGateOrder Id",
          field: "flyGateOrderId",
        },
        {
          label: "Order Amount",
          field: "orderAmount",
          type: "number",
        },
        {
          label: "Paid FOP",
          field: "paidFOP",
        },
        {
          label: "Vendor TxnRef. Number",
          field: "vendorTxnRefNumber",
        },
        {
          label: "Paid Date",
          field: "paidDate",
        },
        {
          label: "Refund Merchant Request Code",
          field: "refundMerchantRequestCode",
        },
        {
          label: "Refund Reference Code",
          field: "refundReferenceCode",
        },
        {
          label: "Refund Requested Amount",
          field: "refundRequestedAmount",
          type: "number",
        },
        {
          label: "Ticket Number",
          field: "originalTicketNumber",
        },
        {
          label: "Approver Name",
          field: "approverFirstName",
        },
        {
          label: "Status",
          field: "refundStatus",
        },
        // {
        //   label: "Action",
        //   field: "action",
        // },
      ],
      paidPaymentOptions: [
        { value: 0, text: "--select Paid Payment Option--" },
      ],
      refundStatusOptions: [
        { value: "0", text: "All" },
        { value: "1", text: "Pending" },
        { value: "2", text: "Submitted" },
        { value: "3", text: "Approved" },
        { value: "4", text: "Sent" },
        { value: "5", text: "Rejected" },
        { value: "6", text: "Completed" },
      ],
      statusOptions: [
        { value: "0", text: "All" },
        { value: "1", text: "Pending" },
        { value: "2", text: "Approved" },
        { value: "3", text: "Canceled" },
      ],
      searchTerm: "",
      editabelData: {},
      sheets: [{ name: "Detail Refund Requests", data: [] }],
    };
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        1: "light-success",
        0: "light-danger",
        /* eslint-enable key-spacing */
      };

      return (status) => statusColor[status];
    },
  },
  created() {
    this.fetchData();
    this.fetchallPaymentOptions();
  },
  methods: {
    fetchallPaymentOptions() {
      this.isLoading = true;
      this.$store.dispatch("config/getActionPrivilege", "Order-View");
      axios
        .get(
          "/PaymentOptions/api/V1.0/PaymentOptions/ViewMerchantPaymentOptions"
        )
        .then((res) => {
          res.paymentOptions.forEach((item) => {
            this.paidPaymentOptions.push({
              value: item.paymentOption,
              text: item.name,
            });
          });
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    fetchData() {
      this.$store.dispatch("config/getActionPrivilege", "Refund-MyQueue");
      this.isLoading = true;
      axios
        .get("Order/api/V1.0/Refund/GetAllCurrentDateRefundQueues")
        .then((res) => {
          this.sheets[0].data = [];
          this.rows = res.refundQueues;
          res.refundQueues.map((m) => {
            this.sheets[0].data.push({
              "Refund Requested Date": m.refundRequestedDateString,
              "First Name": m.firstName,
              "Last Name": m.lastName,
              "Custom Order Code": m.customOrderCode,
              "FlyGate OrderId": m.flyGateOrderId,
              "Order Amount": m.orderAmount,
              "Paid FOP": m.paidFOP,
              "Vendor TxnRef. Number": m.vendorTxnRefNumber,
              "Paid Date": m.paidDate,
              "Refund Merchant Request Code": m.refundMerchantRequestCode,
              "Refund Reference Code": m.refundReferenceCode,
              "Refund Requested Amount": m.refundRequestedAmount,
              "Ticket Number": m.originalTicketNumber,
              "Approvers Name": m.approverFirstName + " " + m.approverLastName,
              "Refund Status":
                m.refundStatus == 0
                  ? "Refund Not Requested"
                  : m.refundStatus == 1
                  ? "Refund Pending"
                  : m.refundStatus == 2
                  ? "Refund Submitted"
                  : m.refundStatus == 3
                  ? "Refund Approved"
                  : m.refundStatus == 4
                  ? "Refund Sent"
                  : m.refundStatus == 5
                  ? "Refund Rejected"
                  : m.refundStatus == 6
                  ? "Refund Completed"
                  : null,
            });
          });
          this.hasData = true;
          this.isLoading = false;
        })
        .catch((res) => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: `${res.data.message}`,
            },
          });
          this.isLoading = false;
        });
    },
    filterRefundQueue() {
      if (this.trxnDateRangePaidDate !== "") {
        const [orderSttDate, orderEdDate] =
          this.trxnDateRangePaidDate.split(" to ");
        if (this.trxnDateRangePaidDate.includes("to")) {
          this.paidStartDate = `${orderSttDate.trim()}`;
          this.paidEndDate = `${orderEdDate.trim()}`;
        } else {
          this.paidStartDate = this.trxnDateRangePaidDate;
          this.paidEndDate = this.trxnDateRangePaidDate;
        }
      }

      if (this.trxnDateRangeRefund !== "") {
        const [trxnSttDate, trxnEdDate] =
          this.trxnDateRangeRefund.split(" to ");
        if (this.trxnDateRangeRefund.includes("to")) {
          this.refundEndDate = `${trxnEdDate.trim()}`;
          this.refundStartDate = `${trxnSttDate.trim()}`;
        } else {
          this.refundStartDate = this.trxnDateRangeRefund;
          this.refundEndDate = this.trxnDateRangeRefund;
        }
      }
      console.log(this.refundStartDate);
      const url = `/Order/api/V1.0/Refund/FilterRefundQueues?flyGateOrderId=${this.filterflyGateOrderId}&vendorTxnRefNumber=${this.filtervendorTxnRefNumber}&customOrderCode=${this.filtercustomOrderCode}&refundReferenceCode=${this.filterrefundReferenceCode}&refundMerchantRequestCode=${this.filterrefundMerchantRequestCode}&paidFOP=${this.selectedPaidPaymentOptions}&fromRefundRequestedDate=${this.refundStartDate}&toRefundRequestedDate=${this.refundEndDate}&fromPaidDate=${this.paidStartDate}&toPaidDate=${this.paidEndDate}&fromRequestedAmount=${this.fromRequestedAmount}&toRequestedAmount=${this.toRequestedAmount}`;
      // Api call
      this.isLoading = true;
      axios
        .get(url)
        .then((res) => {
          this.rows = res.refundQueues;
          this.isLoading = false;
          res.refundQueues.map((m) => {
            this.sheets[0].data.push({
              "Refund Requested Date": m.refundRequestedDateString,
              "First Name": m.firstName,
              "Last Name": m.lastName,
              "Custom Order Code": m.customOrderCode,
              "FlyGate OrderId": m.flyGateOrderId,
              "Order Amount": m.orderAmount,
              "Paid FOP": m.paidFOP,
              "Vendor TxnRef. Number": m.vendorTxnRefNumber,
              "Paid Date": m.paidDate,
              "Refund Merchant Request Code": m.refundMerchantRequestCode,
              "Refund Reference Code": m.refundReferenceCode,
              "Refund Requested Amount": m.refundRequestedAmount,
              "Ticket Number": m.originalTicketNumber,
              "Approvers Name": m.approverFirstName + " " + m.approverLastName,
              "Refund Status":
                m.refundStatus == 0
                  ? "Refund Not Requested"
                  : m.refundStatus == 1
                  ? "Refund Pending"
                  : m.refundStatus == 2
                  ? "Refund Submitted"
                  : m.refundStatus == 3
                  ? "Refund Approved"
                  : m.refundStatus == 4
                  ? "Refund Sent"
                  : m.refundStatus == 5
                  ? "Refund Rejected"
                  : m.refundStatus == 6
                  ? "Refund Completed"
                  : null,
            });
          });

          this.hasData = true;
          this.refundStartDate = "";
          this.refundEndDate = "";
          this.paidStartDate = "";
          this.paidEndDate = "";
        })
        .catch((res) => {
          this.isLoading = false;
          this.rows = [];
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: `${res.data.message}`,
            },
          });
          // this.$refs.loginForm.setErrors(error)
        });
      this.refundStartDate = "";
      this.refundEndDate = "";
      this.paidStartDate = "";
      this.paidEndDate = "";
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
